import React, { useState } from 'react';
import { usePWA } from '../hooks/usePWA';
import { 
  DevicePhoneMobileIcon, 
  ArrowDownTrayIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

interface PWAInstallButtonProps {
  className?: string;
  showOnMobileOnly?: boolean;
}

export const PWAInstallButton: React.FC<PWAInstallButtonProps> = ({ 
  className = '', 
  showOnMobileOnly = true 
}) => {
  const { isInstallable, isInstalled, isMobile, installApp } = usePWA();
  const [isInstalling, setIsInstalling] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  // Non mostrare se:
  // 1. È già installata
  // 2. Non è installabile E non è mobile (per iOS)
  // 3. È configurato per mostrare solo su mobile ma non è mobile
  if (isInstalled || (!isInstallable && !isMobile) || (showOnMobileOnly && !isMobile)) {
    return null;
  }

  const handleInstall = async () => {
    if (isMobile && !isInstallable) {
      // Su iOS o altri dispositivi che non supportano beforeinstallprompt
      setShowInstructions(true);
      return;
    }

    setIsInstalling(true);
    try {
      const success = await installApp();
      if (success) {
        console.log('✅ PWA installata con successo');
      } else {
        console.log('❌ Installazione PWA annullata');
      }
    } catch (error) {
      console.error('❌ Errore installazione PWA:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  const getInstallText = () => {
    if (isInstalling) return 'Installazione...';
    if (isMobile && !isInstallable) return 'Installa App';
    return 'Installa App';
  };

  const getInstallIcon = () => {
    if (isInstalling) return <ArrowDownTrayIcon className="h-5 w-5 animate-bounce" />;
    if (isMobile && !isInstallable) return <DevicePhoneMobileIcon className="h-5 w-5" />;
    return <ArrowDownTrayIcon className="h-5 w-5" />;
  };

  return (
    <>
      <button
        onClick={handleInstall}
        disabled={isInstalling}
        className={`flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium transition-all duration-200 hover:from-blue-700 hover:to-purple-700 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      >
        {getInstallIcon()}
        <span>{getInstallText()}</span>
      </button>

      {/* Modal con istruzioni per iOS */}
      {showInstructions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Installa l'App
              </h3>
              <button
                onClick={() => setShowInstructions(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-semibold text-sm">1</span>
                </div>
                <div>
                  <p className="text-gray-900 font-medium">Tocca il pulsante Condividi</p>
                  <p className="text-gray-600 text-sm">Nella barra degli indirizzi del browser</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-semibold text-sm">2</span>
                </div>
                <div>
                  <p className="text-gray-900 font-medium">Seleziona "Aggiungi alla schermata Home"</p>
                  <p className="text-gray-600 text-sm">Scorri verso il basso se necessario</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-semibold text-sm">3</span>
                </div>
                <div>
                  <p className="text-gray-900 font-medium">Tocca "Aggiungi"</p>
                  <p className="text-gray-600 text-sm">L'app sarà installata sulla schermata Home</p>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex space-x-3">
              <button
                onClick={() => setShowInstructions(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Chiudi
              </button>
              <button
                onClick={() => setShowInstructions(false)}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <CheckIcon className="h-4 w-4 inline mr-1" />
                Fatto
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
